import Loader from "@/components/Common/kpi/Loader";
import {Tooltip} from "@/components/Common/Tooltip";
import clsxm from "@/lib/clsxm";
import type {TButtonStyle} from "@/store/types";
import {ButtonHTMLAttributes, FC, MouseEvent, PropsWithChildren, useId} from "react";

const Button: FC<
  PropsWithChildren & {
    buttonStyle?: TButtonStyle;
    onClick?(e: MouseEvent<HTMLButtonElement>): void;
    className?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    progress?: number | null;
    disabledTooltipText?: string;
    shouldScaleOnHover?: boolean;
    shouldPreventDefault?: boolean;
    shouldStopPropagation?: boolean;
    customButtonId?: string;
    dataTestId?: string;
  } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled">
> = ({
  buttonStyle = "Custom",
  className,
  shouldScaleOnHover = true,
  shouldPreventDefault = true,
  shouldStopPropagation = true,
  isDisabled,
  children,
  onClick,
  isLoading,
  progress,
  disabledTooltipText,
  customButtonId,
  dataTestId,
  ...rest
}) => {
  const buttonId = useId();
  const id = useId();
  return (
    <>
      <button
        id={customButtonId || buttonId}
        data-testid={dataTestId}
        className={clsxm(
          "relative flex h-14 cursor-pointer items-center justify-center rounded-md p-2 text-base font-medium transition-all duration-150 enabled:hover:shadow-xs disabled:cursor-not-allowed",
          buttonStyle === "Gradient" &&
            "disabled:bg-neutral-grey-light-dark disabled:text-neutral-grey-light enabled:bg-linear-(--button-gradient) enabled:text-white enabled:active:scale-95",
          buttonStyle === "Blue" &&
            "bg-primary-dark-solid enabled:hover:bg-primary-medium-solid disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          buttonStyle === "Green" &&
            "bg-success-medium-green enabled:hover:bg-success-herbal disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          buttonStyle === "White" &&
            "border-primary-light-solid enabled:text-primary-dark-solid enabled:hover:bg-primary-light-solid enabled:hover:text-primary-medium-solid disabled:bg-neutral-white-dark disabled:text-neutral-grey-light bg-white enabled:border enabled:active:scale-95",
          buttonStyle === "Danger" &&
            "bg-danger-medium-red enabled:hover:bg-danger-medium-red/80 disabled:bg-neutral-white-dark disabled:text-neutral-grey-light enabled:text-white enabled:active:scale-95",
          shouldScaleOnHover && buttonStyle !== "Custom" && "enabled:hover:scale-[1.01]",
          progress != null && "cursor-progress",
          className,
        )}
        type="button"
        onClick={e => {
          if (shouldPreventDefault) e.preventDefault();
          if (shouldStopPropagation) e.stopPropagation();
          if (progress != null) return;
          onClick && onClick(e);
        }}
        disabled={isDisabled || (isLoading && progress == null) || !onClick}
        {...rest}>
        {isLoading && progress == null ? <Loader /> : children}
        {progress != null && (
          <span className="ml-2 w-10 text-right text-sm font-semibold tabular-nums opacity-70">
            {Math.round(progress)}%
          </span>
        )}
        {progress != null && (
          <span className="absolute inset-x-3 bottom-1.5 h-1 rounded-full bg-current/25">
            <span
              className="block h-full rounded-full bg-current transition-[width] duration-200"
              style={{width: `${progress}%`}}
            />
          </span>
        )}
        {isDisabled && !!disabledTooltipText && (
          <div className="absolute inset-0 z-0" data-tip data-tooltip-id={`button-${id}`} />
        )}
      </button>
      {!!disabledTooltipText && isDisabled && (
        <Tooltip id={`button-${id}`} place="bottom" variant="dark" className="z-50">
          {disabledTooltipText}
        </Tooltip>
      )}
    </>
  );
};

export default Button;
